/*
  <split-hero-slider> — drives the Split Hero section (sections/split-hero.liquid).

  Slides are stacked in one CSS grid cell (see assets/section-split-hero.css),
  so this element only has to move classes and transforms around; it never
  measures or sets heights.

  Two transitions:
    fade  — CSS cross-dissolves on .is-active.
    slide — the incoming slide is parked off-screen for one frame with
            transitions suppressed, then both slides translate together so the
            outgoing one is pushed out of the overflow-hidden viewport.

  Navigation is dots only (plus swipe and arrow keys) — there are deliberately
  no arrow buttons and no play/pause button. Autoplay therefore pauses on hover,
  on keyboard focus, while the section is off-screen, while the tab is hidden,
  and whenever the merchant selects a block in the theme editor.
*/
class SplitHeroSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.viewport = this.querySelector('.split-hero__viewport');
    this.slides = Array.from(this.querySelectorAll('.split-hero__slide'));
    if (!this.viewport || this.slides.length === 0) return;

    this.transition = this.dataset.transition === 'fade' ? 'fade' : 'slide';
    this.interval = Math.max(1000, parseInt(this.dataset.interval, 10) || 6000);
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';

    this.dots = Array.from(this.querySelectorAll('.split-hero__dot'));

    this.index = 0;
    this.isPlaying = false;
    // Held while the merchant has a slide selected in the theme editor; hover
    // and focus handlers respect it so rotation does not resume under them.
    this.userPaused = false;
    this.isVisible = true;

    this.classList.add('is-ready');
    this.setActive(0);

    if (this.slides.length > 1) {
      this.bindControls();
      this.bindSwipe();
      this.bindEditorEvents();
      this.observeVisibility();

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stop();
        } else if (!this.userPaused) {
          this.start();
        }
      });

      if (this.autoplayEnabled) this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
  }

  bindControls() {
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.goTo(i, i > this.index ? 1 : -1);
        this.restartIfPlaying();
      });
    });

    // Arrow keys move between slides when the slider holds focus.
    this.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const forward = event.key === 'ArrowRight';
      this.goTo(forward ? this.index + 1 : this.index - 1, forward ? 1 : -1);
      this.restartIfPlaying();
    });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => {
        if (!this.userPaused) this.start();
      });
    }

    // Always pause for keyboard users while focus is inside the slider.
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => {
      if (!this.userPaused && !this.contains(document.activeElement)) this.start();
    });
  }

  bindSwipe() {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.viewport.addEventListener(
      'touchstart',
      event => {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        tracking = true;
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchend',
      event => {
        if (!tracking) return;
        tracking = false;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;
        // Only act on a clearly horizontal swipe so vertical page scrolling
        // never changes slides.
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;
        this.goTo(deltaX < 0 ? this.index + 1 : this.index - 1, deltaX < 0 ? 1 : -1);
        this.restartIfPlaying();
      },
      { passive: true }
    );
  }

  // In the theme editor, jump to the slide whose block the merchant selected
  // and hold there until they deselect it.
  bindEditorEvents() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    this.addEventListener('shopify:block:select', event => {
      const slide = event.target.closest('.split-hero__slide');
      if (!slide) return;
      const index = this.slides.indexOf(slide);
      if (index === -1) return;
      this.goTo(index, index > this.index ? 1 : -1);
      this.userPaused = true;
      this.stop();
    });

    this.addEventListener('shopify:block:deselect', () => {
      this.userPaused = false;
      this.start();
    });
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) return;

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        this.isVisible = entries[0].isIntersecting;
        if (!this.isVisible) {
          this.stop();
        } else if (!this.userPaused) {
          this.start();
        }
      },
      { threshold: 0.15 }
    );

    this.intersectionObserver.observe(this);
  }

  start() {
    if (!this.autoplayEnabled || this.isPlaying || !this.isVisible || document.hidden) return;
    this.isPlaying = true;
    this.timer = setInterval(() => this.goTo(this.index + 1, 1), this.interval);
  }

  stop() {
    this.isPlaying = false;
    clearInterval(this.timer);
  }

  restartIfPlaying() {
    if (this.userPaused || !this.autoplayEnabled) return;
    this.stop();
    this.start();
  }

  goTo(rawIndex, direction) {
    const total = this.slides.length;
    const index = ((rawIndex % total) + total) % total;
    if (index === this.index) return;

    const from = this.slides[this.index];
    const to = this.slides[index];

    if (this.transition === 'slide') {
      // Park the incoming slide off-screen with transitions suppressed, flush
      // that style, then let both slides animate to their new positions.
      to.classList.add('split-hero__slide--staged');
      to.style.transform = `translateX(${direction > 0 ? 100 : -100}%)`;
      void to.offsetWidth;
      to.classList.remove('split-hero__slide--staged');
      to.style.transform = 'translateX(0)';
      from.style.transform = `translateX(${direction > 0 ? -100 : 100}%)`;
    } else {
      to.style.transform = '';
      from.style.transform = '';
    }

    this.index = index;
    this.setActive(index);
  }

  setActive(index) {
    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      // Keep off-screen slides out of the tab order.
      slide.querySelectorAll('a, button').forEach(node => {
        if (isActive) {
          node.removeAttribute('tabindex');
        } else {
          node.setAttribute('tabindex', '-1');
        }
      });
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

customElements.define('split-hero-slider', SplitHeroSlider);
